/**
 * Custom React Hook for Car Chat Engine
 * Manages chat state, message history, and AI responses
 * Supports multi-vehicle systems (light commercial EV, city car, compressor)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { CarChatEngine } from '../utils/carChatEngine';

export const useChatEngine = (vehicleData, vehicleType = 'nexon') => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const chatEngineRef = useRef(null);

  // Initialize chat engine with vehicle type
  useEffect(() => {
    if (!chatEngineRef.current) {
      chatEngineRef.current = new CarChatEngine(vehicleData, vehicleType);
    } else {
      // Update vehicle type if changed
      chatEngineRef.current.setVehicleType(vehicleType);
    }
  }, [vehicleType]);

  // Update vehicle data in engine when it changes
  useEffect(() => {
    if (chatEngineRef.current && vehicleData) {
      chatEngineRef.current.updateVehicleData(vehicleData);
      // Update suggestions based on new vehicle state
      updateSuggestions();
    }
  }, [vehicleData]);

  // Update smart suggestions
  const updateSuggestions = useCallback(() => {
    if (chatEngineRef.current) {
      const newSuggestions = chatEngineRef.current.getSuggestions();
      setSuggestions(newSuggestions);
    }
  }, []);

  // Send message to AI
  const sendMessage = useCallback(async (message) => {
    if (!message.trim() || !chatEngineRef.current) return;

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      message: message.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Get AI response
      const response = await chatEngineRef.current.chat(message.trim());

      // Add AI response (with Knowledge Graph fields if available)
      const aiMessage = {
        id: Date.now() + 1,
        role: 'ai',
        message: response.message,
        intent: response.intent,
        timestamp: response.timestamp,
        // Knowledge Graph fields
        confidence: response.confidence,
        citations: response.citations,
        reasoningPath: response.reasoningPath,
        // NEW: Hybrid RAG fields
        retrievalDetails: response.retrievalDetails,
        isHybrid: response.isHybrid,
        // NEW: Multimodal content
        images: response.images || [],
        tables: response.tables || [],
        hasVisualContent: response.hasVisualContent || false
      };

      console.log('[useChatEngine] 🎨 AI MESSAGE WITH MULTIMODAL CONTENT:');
      console.log('[useChatEngine] 📷 Images:', aiMessage.images.length);
      console.log('[useChatEngine] 📊 Tables:', aiMessage.tables.length);
      console.log('[useChatEngine] ✨ Has Visual Content:', aiMessage.hasVisualContent);

      setMessages(prev => [...prev, aiMessage]);

      // Update suggestions after interaction
      updateSuggestions();
    } catch (error) {
      console.error('Error sending message:', error);

      const errorMessage = {
        id: Date.now() + 1,
        role: 'ai',
        message: "I'm experiencing technical difficulties. Please try again.",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [updateSuggestions]);

  // Send quick action query
  const sendQuickAction = useCallback((query) => {
    sendMessage(query);
  }, [sendMessage]);

  // Clear chat history
  const clearChat = useCallback(() => {
    if (chatEngineRef.current) {
      chatEngineRef.current.clearHistory();
    }
    setMessages([]);
    setInputValue('');
    updateSuggestions();
  }, [updateSuggestions]);

  // Export conversation
  const exportConversation = useCallback(() => {
    if (chatEngineRef.current) {
      return chatEngineRef.current.exportConversation();
    }
    return '';
  }, []);

  // Get conversation context
  const getContext = useCallback(() => {
    if (chatEngineRef.current) {
      return chatEngineRef.current.getContext();
    }
    return null;
  }, []);

  return {
    messages,
    isLoading,
    inputValue,
    setInputValue,
    suggestions,
    sendMessage,
    sendQuickAction,
    clearChat,
    exportConversation,
    getContext
  };
};

export default useChatEngine;
